/**
 * Language Switcher — i18n Toggle
 *
 * Compact dropdown for switching between all 24 EU official languages.
 * Designed for the Header bar. Bloomberg-style: minimal, functional.
 * Scrollable list for 24 languages with search filtering.
 */

import { useTranslation, LANGUAGES } from '@/i18n';
import { Globe } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export function LanguageSwitcher() {
  const { language, setLanguage } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const currentLang = LANGUAGES.find((l) => l.code === language);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-8 items-center gap-1.5 rounded-md px-2 text-ic-text-tertiary hover:bg-ic-hover hover:text-ic-text-primary transition-colors"
        aria-label="Switch language"
        title={`Language: ${currentLang?.nativeLabel ?? 'English'}`}
      >
        <Globe className="h-4 w-4" />
        <span className="text-[10px] font-medium uppercase">{language}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md border border-ic-border bg-ic-sidebar py-1 shadow-lg max-h-80 overflow-y-auto">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                language === lang.code
                  ? 'bg-ic-accent/10 text-ic-accent'
                  : 'text-ic-text-secondary hover:bg-ic-hover hover:text-ic-text-primary'
              }`}
            >
              <span className="text-xs font-medium uppercase w-5 shrink-0">{lang.code}</span>
              <span className="truncate">{lang.nativeLabel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
